import { CartItems, CartItemsProps } from 'ui/components/CartItems';
import { ActiveCartItems } from 'types/cart';
import { GiftCardCartItem, ProductCartItem } from '__generated__/graphql';

import { GroupedCartItemsTitle } from './GroupedCartItemsTitle';

enum SortDirection {
  ASC = 'ASC', // oldest date first
  DESC = 'DESC', // most recent date first
}
type GroupCartItems = {
  items?: ActiveCartItems;
  sortDirection?: SortDirection;
};

const sortByEstimatedDeliveryDates = (
  productMap,
  sortDirection = SortDirection.ASC
) => {
  const sorted = [
    [...productMap.entries()].sort(([a], [b]) => {
      const dateA = Date.parse(a);
      const dateB = Date.parse(b);
      if (isNaN(dateA) || isNaN(dateB)) return 1;
      return sortDirection === SortDirection.DESC
        ? dateB - dateA
        : dateA - dateB;
    }),
  ];
  return new Map(...sorted);
};

const groupCartItemsByDeliveryDate = ({
  items = [] as ActiveCartItems,
  sortDirection,
}: GroupCartItems) => {
  const itemMap = new Map();
  const giftCards: GiftCardCartItem[] = [];
  const unavailableEddProducts: ProductCartItem[] = [];

  // Group products by eddEnd date using the itemMap
  items.forEach(item => {
    if ('estimatedDeliveryDate' in item) {
      const eddEnd = (item as ProductCartItem).estimatedDeliveryDate?.eddEnd;

      // If eddEnd exists & date is already in itemMap, add the product
      if (eddEnd && itemMap.has(eddEnd)) {
        itemMap.set(eddEnd, [...itemMap.get(eddEnd), item]);
      }
      // If eddEnd exists & date is not in itemMap, create new entry for it
      else if (eddEnd && !itemMap.has(eddEnd)) {
        itemMap.set(eddEnd, [item]);
      }
      // If eddEnd doesn't exist, add to unavailable array
      else {
        unavailableEddProducts.push(item as ProductCartItem);
      }
    } else if ('senderName' in item) {
      giftCards.push(item);
    } else {
      unavailableEddProducts.push(item as ProductCartItem);
    }
  });

  // Sort cart items by eddEnd date (the key) & add unavailable and gift card items
  const sortedCartItems = sortByEstimatedDeliveryDates(itemMap, sortDirection);
  if (unavailableEddProducts.length) {
    sortedCartItems.set('unavailable', unavailableEddProducts);
  }
  if (giftCards.length) {
    sortedCartItems.set('giftCards', giftCards);
  }

  return sortedCartItems;
};

export const GroupedCartItems = ({
  basketId,
  products = [],
  editableItems,
  preventItemLinks,
}: CartItemsProps) => {
  const groupedItems = groupCartItemsByDeliveryDate({
    items: products as ActiveCartItems,
  });

  return (
    <>
      {[...groupedItems?.entries()]?.map(group => {
        const groupKey = group[0] as string;
        const groupProducts = group[1] as ActiveCartItems;

        return (
          <div key={groupKey}>
            <GroupedCartItemsTitle group={groupKey} />
            {groupProducts?.length ? (
              <CartItems
                basketId={basketId}
                condensedView={true}
                editableItems={editableItems}
                preventItemLinks={preventItemLinks}
                products={groupProducts}
              />
            ) : null}
          </div>
        );
      })}
    </>
  );
};
